export const SORT_DEFAULT = 'default'
export const SORT_LANGUAGE_ASC = 'language_asc'
export const SORT_GENDER_ASC = 'gender_asc'

export const SORT_LANGUAGE_FIRST_PREFIX = 'language_first::'
export const SORT_GENDER_FIRST_PREFIX = 'gender_first::'

export const SORT_OPTIONS = [
  { key: SORT_DEFAULT, label: 'Default order' },
  { key: SORT_LANGUAGE_ASC, label: 'Language (A-Z)' },
  { key: SORT_GENDER_ASC, label: 'Gender (A-Z)' }
]

const getValidPreviewValue = value => {
  if (typeof value !== 'string') return ''
  const trimmed = value.trim()
  const normalized = trimmed.toLowerCase()
  if (!trimmed || normalized === 'null' || normalized === 'undefined') {
    return ''
  }
  return trimmed
}

export const getVoicePreviewUrl = voice => {
  const candidates = [
    voice?.audio_url,
    voice?.audioUrl,
    voice?.preview_url,
    voice?.previewUrl
  ]

  for (let index = 0; index < candidates.length; index += 1) {
    const resolved = getValidPreviewValue(candidates[index])
    if (resolved) return resolved
  }

  return ''
}

export const formatMetaValue = value => {
  if (value === null || value === undefined) return 'N/A'
  if (typeof value === 'string' && value.trim() === '') return 'N/A'
  return value
}

export const getVoiceMetaItems = voice => {
  const rawItems = [
    { label: 'Language', value: voice.language },
    { label: 'Accent', value: voice.accent },
    { label: 'Gender', value: voice.gender },
    { label: 'Age Range', value: voice.age_range }
  ]

  return rawItems
    .map(item => ({ ...item, value: formatMetaValue(item.value) }))
    .filter(item => item.value !== 'N/A')
}

const getFallbackVoiceLabel = (variant, index) => {
  const numericLabel = String(index + 1).padStart(2, '0')

  if (variant === 'recommended') return `Preferred Voice ${numericLabel}`
  if (variant === 'custom') return `Custom Voice ${numericLabel}`
  return `Default Voice ${numericLabel}`
}

export const getVoiceDisplayName = (voice, variant, index) => {
  const primaryName = formatMetaValue(voice?.name)
  if (primaryName !== 'N/A') return primaryName

  const secondaryName = formatMetaValue(
    voice?.voice_id || voice?.voice_name || voice?.label || voice?.id
  )
  if (secondaryName !== 'N/A') return secondaryName

  return getFallbackVoiceLabel(variant, index)
}

export const getVoiceReasoningPreview = voice => {
  const reasoning = formatMetaValue(voice?.reasoning)
  if (reasoning === 'N/A') return ''
  return String(reasoning)
}

export const getVoiceDescription = (voice, variant) => {
  if (variant === 'recommended') {
    return voice.reasoning || voice.description || 'Recommended for your story.'
  }

  if (variant === 'custom') {
    return (
      voice.description ||
      'Your uploaded voice, ready to use anywhere this narration style fits.'
    )
  }

  return voice.description || 'A platform voice from the built-in library.'
}

export const getSourceIcon = variant => {
  if (variant === 'recommended') return 'star'
  if (variant === 'custom') return 'user'
  return 'customer-service'
}

export const getVoiceSubtitle = (voice, variant) => {
  if (variant === 'recommended' && typeof voice.rank === 'number') {
    return `Rank ${voice.rank}`
  }

  if (variant === 'system') {
    return ''
  }

  const gender = formatMetaValue(voice?.gender)
  if (gender !== 'N/A') {
    return `Gender: ${gender}`
  }

  return ''
}

export const getVoiceGenderLabel = voice => {
  const gender = formatMetaValue(voice?.gender)
  return gender === 'N/A' ? '' : String(gender)
}

export const getVoiceGenderTag = voice => {
  const label = getVoiceGenderLabel(voice)
    .trim()
    .toLowerCase()
  if (!label) return 'U'
  if (label.startsWith('female')) return 'F'
  if (label.startsWith('male')) return 'M'
  return String(label.charAt(0)).toUpperCase()
}

export const getVoiceMetaBadges = voice => {
  const genderValue = formatMetaValue(voice?.gender)
  if (genderValue === 'N/A') return []

  return [
    {
      key: 'gender',
      label: 'Gender',
      value: genderValue
    }
  ]
}

const normalizeValue = value => {
  if (typeof value !== 'string') return ''
  return value.trim().toLowerCase()
}

const toReadableLabel = value =>
  String(value)
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')

export const formatOptionLabel = value => {
  const label = toReadableLabel(value)
  if (!label) return ''
  return label.charAt(0).toUpperCase() + label.slice(1)
}

export const getSortOptionLabel = sortBy => {
  const selectedOption = SORT_OPTIONS.find(option => option.key === sortBy)
  if (selectedOption) return selectedOption.label

  if (
    typeof sortBy === 'string' &&
    sortBy.startsWith(SORT_LANGUAGE_FIRST_PREFIX)
  ) {
    const value = sortBy.replace(SORT_LANGUAGE_FIRST_PREFIX, '')
    return `Language first: ${formatOptionLabel(value)}`
  }

  if (
    typeof sortBy === 'string' &&
    sortBy.startsWith(SORT_GENDER_FIRST_PREFIX)
  ) {
    const value = sortBy.replace(SORT_GENDER_FIRST_PREFIX, '')
    return `Gender first: ${formatOptionLabel(value)}`
  }

  return 'Default order'
}

export const getSortValues = (voiceList = [], field) => {
  if (!Array.isArray(voiceList)) return []

  const valuesByNormalized = new Map()

  voiceList.forEach(voice => {
    const raw = voice?.[field]
    const normalized = normalizeValue(raw)
    if (!normalized || valuesByNormalized.has(normalized)) return
    valuesByNormalized.set(normalized, String(raw).trim())
  })

  return Array.from(valuesByNormalized.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

export const sortVoices = (voiceList = [], sortBy = SORT_DEFAULT) => {
  if (
    !Array.isArray(voiceList) ||
    voiceList.length <= 1 ||
    sortBy === SORT_DEFAULT
  ) {
    return voiceList
  }

  const preferredLanguage =
    typeof sortBy === 'string' && sortBy.startsWith(SORT_LANGUAGE_FIRST_PREFIX)
      ? sortBy.replace(SORT_LANGUAGE_FIRST_PREFIX, '')
      : ''
  const preferredGender =
    typeof sortBy === 'string' && sortBy.startsWith(SORT_GENDER_FIRST_PREFIX)
      ? sortBy.replace(SORT_GENDER_FIRST_PREFIX, '')
      : ''

  const getComparableValue = voice => {
    if (sortBy === SORT_LANGUAGE_ASC) return normalizeValue(voice?.language)
    if (sortBy === SORT_GENDER_ASC) return normalizeValue(voice?.gender)
    if (preferredLanguage) return normalizeValue(voice?.language)
    if (preferredGender) return normalizeValue(voice?.gender)
    return ''
  }

  return voiceList
    .map((voice, index) => ({ voice, index }))
    .sort((a, b) => {
      const valueA = getComparableValue(a.voice)
      const valueB = getComparableValue(b.voice)
      const hasValueA = Boolean(valueA)
      const hasValueB = Boolean(valueB)

      if (preferredLanguage || preferredGender) {
        const preferredValue = preferredLanguage || preferredGender
        const aIsPreferred = valueA === preferredValue
        const bIsPreferred = valueB === preferredValue

        if (aIsPreferred && !bIsPreferred) return -1
        if (!aIsPreferred && bIsPreferred) return 1
      }

      if (hasValueA && hasValueB) {
        const comparison = valueA.localeCompare(valueB)
        if (comparison !== 0) return comparison
      } else if (hasValueA && !hasValueB) {
        return -1
      } else if (!hasValueA && hasValueB) {
        return 1
      }

      return a.index - b.index
    })
    .map(item => item.voice)
}
